<template>
	<div class="sub-bar-dropdown">
		<SubResource
			:iconName="getPanelResource(6) ?? ResourceType.COINS"
			:value="getResourceValue(getPanelResource(6))"
			:progress="getResourceProgress(getPanelResource(6))"
			:isMax="isMax(getPanelResource(6))"
			:max="getResourceStorageValue(getPanelResource(6))"
			:unlockLevel="getUnlockLevel(getPanelResource(6))"
			:isOverMax="isOverMax(getPanelResource(6))"
			:isLocked="isResourceLocked(getPanelResource(6))"
			:dividerShape="[0, 0, 0, 1]"
			:hasDivider="false"
		/>
		<SubResource
			:iconName="getPanelResource(7) ?? ResourceType.COINS"
			:value="getResourceValue(getPanelResource(7))"
			:progress="getResourceProgress(getPanelResource(7))"
			:isMax="isMax(getPanelResource(7))"
			:max="getResourceStorageValue(getPanelResource(7))"
			:unlockLevel="getUnlockLevel(getPanelResource(7))"
			:isOverMax="isOverMax(getPanelResource(7))"
			:isLocked="isResourceLocked(getPanelResource(7))"
			:dividerShape="[0, 0, 0, 1]"
			:hasDivider="false"
		/>
		<AppTextButton
			class="configure-button"
			:type="StatusTypes.PRIMARY"
			:text="translate('ui.settings')"
			@click="showConfigure"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import ResourcePanelConfigurePopup from "@/components/ResourcePanelConfig/ResourcePanelConfigurePopup.vue";
import SubResource from "@/components/SubResource.vue";
import { ResourceType } from "@/game/entities/config/resources";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore.ts";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { markRaw } from "vue";

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const resourcesStorageStore = useResourcesStorageStore();
const resourcesStorage = storeToRefs(resourcesStorageStore);

const popupStore = markRaw(usePopupStore());
const { resourcePanelConfig } = storeToRefs(useUserStore());

interface Props {
	resourcesUnlocks: Partial<Record<ResourceType, boolean>>;
	unlockLevels: Partial<Record<ResourceType, number>>;
}

const { resourcesUnlocks, unlockLevels } = defineProps<Props>();

const getPanelResource = (index: number): ResourceType => {
	return resourcePanelConfig.value?.[index] as ResourceType;
};

const getResourceValue = (resource?: ResourceType): number => {
	return resources[resource as ResourceType]?.value ?? 0;
};

const getResourceStorageValue = (resource?: ResourceType): number => {
	return resourcesStorage[resource as ResourceType]?.value ?? 0;
};

const getResourceProgress = (resource: ResourceType): number => {
	const current = resources[resource]?.value ?? 0;
	const storage = resourcesStorage[resource]?.value ?? 0;
	return storage === 0 ? 0 : Math.min(current / storage, 1);
};

const getUnlockLevel = (resource?: ResourceType): number | undefined => {
	return resource ? unlockLevels[resource] : undefined;
};

const isMax = (resource?: ResourceType): boolean => {
	const currentValue = getResourceValue(resource);
	const storageValue = getResourceStorageValue(resource);

	return !!resource && currentValue !== 0 && storageValue <= currentValue;
};

const isOverMax = (resource?: ResourceType): boolean => {
	const currentValue = getResourceValue(resource);
	const storageValue = getResourceStorageValue(resource);

	return !!resource && currentValue !== 0 && storageValue < currentValue;
};

const isResourceLocked = (resource?: ResourceType): boolean => {
	return !resource || !resourcesUnlocks[resource];
};

const showConfigure = (): void => {
	popupStore.showPopup({
		type: PopupType.NO_DIVIDER,
		heading: translate("ui.configuration_panel"),
		skin: SkinTypes.LIGHT,
		isPaddingless: true,
		isCentered: true,
		isPriority: true,
		height: 32,
		width: 25.875,
		component: markRaw(ResourcePanelConfigurePopup),
	});
};
</script>

<style lang="scss" scoped>
.sub-bar-dropdown {
	@include flex-col-center;
	position: absolute;
	top: 100%;
	right: 0;
	gap: 0.125rem;
	z-index: 1;
	margin-top: 0.188rem;
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(
		to top,
		rgb(189, 144, 50),
		rgb(240, 211, 134)
	);
	border-image-slice: 1;
	background-color: rgb(25, 24, 33);
	background: url($path-images + "backgrounds/textures/top-menu-texture-bg.avif")
		repeat center;
	padding: 0.125rem;
	width: 8.125rem;

	.sub-resource {
		border-top: 0.125rem solid #464550;
		border-right: 0.125rem solid #111015;
		border-bottom: 0.125rem solid #111015;
		border-left: 0.125rem solid #464550;
		background-color: #2e2d39;
		width: 100%;
		height: 2rem;
	}
	.configure-button {
		width: 100%;
		font-size: 0.9063rem;
	}
}
</style>
